import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import { IconPickerField } from '../../../../commonDesign/IconPicker'
import presets from './presets'

const presetOptions = [
  { label: 'Select a preset...', value: '' },
  ...Object.entries(presets).map(([key, preset]) => ({
    label: preset.displayName,
    value: key
  }))
]

const scopeOptions = [
  { label: 'Client — data for the current client\'s Redtail family', value: 'client' },
  { label: 'My Book — all data for my Redtail user', value: 'user' }
]

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

function NumberField ({ name, control, label, helperText, placeholder }) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <SydLabel label={label} helperText={helperText}>
          <SydInput
            type='number'
            value={field.value ?? ''}
            onChange={(e) => {
              const raw = e.target.value
              if (!raw || !raw.trim()) { field.onChange(null); return }
              const parsed = parseInt(raw, 10)
              field.onChange(isNaN(parsed) ? null : parsed)
            }}
            placeholder={placeholder}
          />
        </SydLabel>
      )}
    />
  )
}

NumberField.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  helperText: PropTypes.string,
  placeholder: PropTypes.string
}

function RedtailListConfigEditor ({ fieldPrefix }) {
  const { control, watch } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`

  const presetValue = watch(`${configPrefix}.preset`)
  const selectedPreset = useMemo(() => {
    return presetValue ? presets[presetValue] : null
  }, [presetValue])

  const isNotesPreset = presetValue === 'latest-notes'
  const isWorkflowsPreset = presetValue === 'workflows'

  const renderPresetFields = () => {
    if (isNotesPreset) {
      return (
        <>
          <Grid item xs={4}>
            <NumberField
              name={`${configPrefix}.pinnedLimit`}
              control={control}
              label='Pinned Limit'
              helperText='Max pinned notes (default 10)'
              placeholder='10'
            />
          </Grid>
          <Grid item xs={4}>
            <NumberField
              name={`${configPrefix}.recentLimit`}
              control={control}
              label='Recent Limit'
              helperText='Max recent notes (default 6)'
              placeholder='6'
            />
          </Grid>
          <Grid item xs={4}>
            <NumberField
              name={`${configPrefix}.notePreviewLength`}
              control={control}
              label='Preview Length'
              helperText='Max chars in preview (default 200)'
              placeholder='200'
            />
          </Grid>
        </>
      )
    }

    if (isWorkflowsPreset) {
      return (
        <Grid item xs={4}>
          <NumberField
            name={`${configPrefix}.limit`}
            control={control}
            label='Limit'
            helperText='Max workflows to show (default 10)'
            placeholder='10'
          />
        </Grid>
      )
    }

    return (
      <>
        <Grid item xs={6}>
          <NumberField
            name={`${configPrefix}.limit`}
            control={control}
            label='Limit'
            helperText='Max items to show (default 5)'
            placeholder='5'
          />
        </Grid>
        <Grid item xs={6}>
          <NumberField
            name={`${configPrefix}.lookaheadDays`}
            control={control}
            label='Lookahead Days'
            helperText='Days into the future (default 90)'
            placeholder='90'
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`${configPrefix}.categoryIds`}
            control={control}
            render={({ field }) => (
              <SydLabel label='Activity Categories' helperText='Comma-separated category IDs (leave blank for all)'>
                <SydInput
                  value={Array.isArray(field.value) ? field.value.join(', ') : ''}
                  onChange={(e) => {
                    const raw = e.target.value
                    if (!raw.trim()) { field.onChange(null); return }
                    const ids = raw.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
                    field.onChange(ids.length ? ids : null)
                  }}
                  placeholder='e.g. 1, 3, 5'
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name={`${configPrefix}.priorityFilter`}
            control={control}
            render={({ field }) => (
              <SydLabel label='Priority Filter' helperText='Comma-separated priority values from Redtail (e.g. Low, Normal, High or 1, 2, 3). Leave blank for all.'>
                <SydInput
                  value={(field.value ?? []).join(', ')}
                  onChange={(e) => {
                    const raw = e.target.value
                    const arr = raw.split(',').map(s => s.trim()).filter(Boolean)
                    field.onChange(arr.length ? arr : null)
                  }}
                  placeholder='e.g. Low, Normal, High'
                />
              </SydLabel>
            )}
          />
        </Grid>
      </>
    )
  }

  return (
    <Grid container spacing={2}>
      {/* ── Core ─────────────────────────────────────── */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Core</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.preset`}
          control={control}
          render={({ field }) => (
            <SydLabel required label='Preset' helperText='Select the list type this component displays'>
              <SydSelect
                options={presetOptions}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.scope`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Scope' helperText='Filter to a specific client or across your full book'>
              <SydSelect
                options={scopeOptions}
                value={field.value || 'client'}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      {/* ── Appearance ───────────────────────────────── */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Appearance</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.title`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Title' helperText='Override the preset default title'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder={selectedPreset?.defaults?.title || 'Title'}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.icon`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Icon' helperText='Icon name (e.g. redtailSmall)'>
              <IconPickerField
                value={field.value || selectedPreset?.defaults?.icon || null}
                onChange={field.onChange}
                placeholder={selectedPreset?.defaults?.icon || 'Select an icon...'}
              />
            </SydLabel>
          )}
        />
      </Grid>
      {/* ── Preset Settings ──────────────────────────── */}
      {selectedPreset ? (
        <>
          <Grid item xs={12}>
            <div style={sectionHeading}>
              {selectedPreset.displayName} Settings
            </div>
          </Grid>
          {renderPresetFields()}
        </>
      ) : null}
    </Grid>
  )
}

RedtailListConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default RedtailListConfigEditor
